import FeatureLandingPage from "@/components/features/FeatureLandingPage";

const SITE = "https://shattaraai.com";

const howItWorks = [
  { title: "Upload Your Notes", description: "Add PDFs, slides, or any study material. Shattara reads and understands the content." },
  { title: "AI Generates Flashcards", description: "Key terms, definitions, and concepts are extracted and turned into flashcard decks automatically." },
  { title: "Study with Spaced Repetition", description: "Cards you struggle with appear more often. Cards you know well fade back. Your study time is optimized." },
];

const benefits = [
  { title: "Save Hours of Manual Work", description: "No more typing out flashcards by hand. Shattara generates them from your materials in seconds." },
  { title: "Spaced Repetition Built In", description: "The SM-2 algorithm schedules reviews at the optimal time so you retain more with less effort." },
  { title: "Covers Your Exact Syllabus", description: "Flashcards are generated from your uploaded content, not generic databases." },
  { title: "Star & Organize Cards", description: "Mark difficult cards and organize decks by topic or chapter." },
  { title: "Track Your Progress", description: "See how many cards are due, how many you've mastered, and where gaps remain." },
];

const faqItems = [
  { question: "Can I edit the AI-generated flashcards?", answer: "Yes. Every generated flashcard can be edited, deleted, or starred. You have full control over your decks." },
  { question: "What is spaced repetition?", answer: "Spaced repetition is a learning technique where you review material at increasing intervals. Shattara uses the SM-2 algorithm to schedule card reviews for optimal long-term retention." },
  { question: "Can I create flashcards from a YouTube video?", answer: "Yes. Upload a YouTube link and Shattara will transcribe the video, then generate flashcards from the spoken content." },
  { question: "How many flashcards can I create?", answer: "Free accounts have daily limits. Paid plans offer unlimited flashcard generation and deck storage." },
  { question: "Can I export my flashcards?", answer: "You can study and manage all flashcards within Shattara. Export features depend on your plan." },
];

const relatedFeatures = [
  { name: "AI Tutor", href: "/ai-tutor", description: "Get step-by-step explanations for any concept in your course materials." },
  { name: "AI Quiz Generator", href: "/ai-quiz-generator", description: "Test your knowledge with auto-generated quizzes from your notes." },
  { name: "AI Exam Generator", href: "/ai-exam-generator", description: "Simulate full exam conditions with timed, scored assessments." },
  { name: "AI Lecture Summarizer", href: "/ai-lecture-summarizer", description: "Condense hours of lectures into clear, structured summaries." },
  { name: "How AI Flashcards Work", href: "/how-ai-flashcards-work", description: "Learn the science of spaced repetition and how AI generates flashcards." },
  { name: "Best AI Study Tools", href: "/best-ai-study-tools", description: "Compare the top AI-powered study tools for students in 2026." },
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Shattara AI Flashcard Generator",
    description: "Automatically generate spaced-repetition flashcards from your PDFs, lecture slides, and videos.",
    url: `${SITE}/ai-flashcards`,
    brand: { "@type": "Organization", name: "Shattara AI" },
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/InStock" },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to Generate AI Flashcards with Shattara",
    description: "Create flashcards automatically from your study materials using Shattara AI.",
    step: howItWorks.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.title,
      text: s.description,
    })),
  },
];

export default function AIFlashcardsPage() {
  return (
    <FeatureLandingPage
      seoTitle="AI Flashcard Generator — Shattara AI"
      seoDescription="Automatically generate flashcards from your notes, PDFs, and slides. Study smarter with AI-powered spaced repetition."
      headline="AI Flashcards — Made from Your Own Notes"
      subtitle="Upload your study materials. Shattara extracts key concepts and builds flashcard decks with built-in spaced repetition."
      howItWorks={howItWorks}
      benefits={benefits}
      faqItems={faqItems}
      relatedFeatures={relatedFeatures}
      structuredData={structuredData}
    />
  );
}
